import { useState, useEffect, useCallback } from 'react';
import { getData } from '../global/func';

/**
 * One of the server's option lists, as [{id, description}].
 *
 * The settings store an id -- "22" for a reciter, a filename for a muezzin --
 * and only this list says what that id is called. Without it the home screen
 * reported the recitation as "Not playing · 22", which is the database's answer
 * to a question nobody asked.
 *
 * Shared by the Sound panel and the home screen so the mapping is fetched and
 * parsed in one place rather than two.
 */
export const useOptions = (deviceKey, option) => {
  const [list, setList] = useState([]);

  const load = useCallback(async () => {
    if (!deviceKey || !option) return;

    const auth = {
      DEVICE_KEY: deviceKey,
      SESSION_ID: import.meta.env.REACT_APP_SESSION_ID,
    };
    const url = encodeURI(
      `${import.meta.env.REACT_APP_API_SERVER_URL}/options/?option=${option}`
    );
    const rows = await getData(url, auth);

    // A failed request used to throw on rows[0] and leave the list empty with
    // nothing said about it.
    if (!Array.isArray(rows) || rows.length === 0) return;

    try {
      const parsed = JSON.parse(rows[0].data);
      setList(parsed.map((e) => ({ id: e.id, description: e.description })));
    } catch (error) {
      console.log(`ERROR: could not read ${option}`, error);
    }
  }, [deviceKey, option]);

  useEffect(() => { load(); }, [load]);

  // Ids come back as numbers from the database and as strings from a select, so
  // the comparison is made on strings deliberately.
  const describe = useCallback(
    (id) => list.find((e) => `${e.id}` === `${id}`)?.description || '',
    [list]
  );

  return { list, describe };
};
